import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Database, Loader2, AlertCircle, Play, RotateCcw, CheckCircle,
  XCircle, Clock, FileText, AlertTriangle, Plus, Eye, RefreshCw,
  Settings, Key, Link2, Save, Wifi, WifiOff, Search, ArrowRight,
  MapPin, Tag,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  running: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

export default function ImportDashboardPage() {
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [errors, setErrors] = useState([]);
  const [reconcReports, setReconcReports] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('connections');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ run_type: 'full', source_system: 'woocommerce', is_dry_run: true, notes: '' });
  const [processing, setProcessing] = useState(false);
  const [connections, setConnections] = useState(null);
  const [connSaving, setConnSaving] = useState({});
  const [connEdits, setConnEdits] = useState({});
  // Connection test results
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  // Source previews
  const [previews, setPreviews] = useState({});
  const [previewing, setPreviewing] = useState({});
  // Category mapping
  const [mappingRules, setMappingRules] = useState(null);
  const [mappingTest, setMappingTest] = useState('');
  const [mappingResults, setMappingResults] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [s, r, e, rec, conn] = await Promise.all([
        api.get('/admin/imports/stats'),
        api.get('/admin/imports/runs'),
        api.get('/admin/imports/errors?is_resolved=false'),
        api.get('/admin/imports/reconciliation'),
        api.get('/admin/connections'),
      ]);
      setStats(s.data); setRuns(r.data); setErrors(e.data); setReconcReports(rec.data); setConnections(conn.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load mapping rules on mount
  useEffect(() => {
    api.post('/admin/imports/preview-mapping', { records: [] }).then(r => setMappingRules(r.data.rules)).catch(() => {});
  }, []);

  const createRun = async () => { try { const r = await api.post('/admin/imports/runs', form); toast.success(`Import run created`); setShowCreate(false); await loadData(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const viewRun = async (id) => { try { const r = await api.get(`/admin/imports/runs/${id}`); setSelectedRun(r.data); } catch { toast.error('Failed to load run'); } };

  // Map a run_type to the source entity the fetch endpoint expects.
  const runEntity = (rt) => ({ orders: 'orders', customers: 'customers', certificates: 'certificates', assets: 'assets', full: 'orders' }[rt] || 'orders');

  // Fetch from the configured source and run it through the import pipeline.
  const fetchAndProcess = async (run, e) => {
    if (e) e.stopPropagation();
    setProcessing(true);
    try {
      const r = await api.post(`/admin/imports/runs/${run.id}/fetch-and-process`, { entity: runEntity(run.run_type), limit: 50 });
      const d = r.data || {};
      toast.success(`${run.is_dry_run ? 'Dry run' : 'Imported'}: ${d.imported ?? 0} imported, ${d.skipped ?? 0} skipped, ${d.errors ?? 0} errors`);
      await loadData();
      if (selectedRun?.id === run.id) await viewRun(run.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fetch & process failed');
    } finally { setProcessing(false); }
  };
  const resolveError = async (id, notes) => { setProcessing(true); try { await api.put(`/admin/imports/errors/${id}/resolve`, { resolution_notes: notes || 'Resolved', action: 'resolve' }); toast.success('Resolved'); await loadData(); } catch { toast.error('Failed'); } finally { setProcessing(false); } };
  const manualCategoryResolve = async (id, cat) => { setProcessing(true); try { await api.put(`/admin/imports/errors/${id}/manual-category`, { category_slug: cat }); toast.success(`Assigned to ${cat}`); await loadData(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setProcessing(false); } };
  const runReconciliation = async () => { setProcessing(true); try { await api.post('/admin/imports/reconciliation', { report_type: 'general' }); toast.success('Report generated'); await loadData(); } catch { toast.error('Failed'); } finally { setProcessing(false); } };

  const updateConnField = (ck, fk, v) => setConnEdits(p => ({...p, [`${ck}.${fk}`]: v}));
  const getConnValue = (ck, f) => { const ek = `${ck}.${f.key}`; return connEdits[ek] !== undefined ? connEdits[ek] : f.value || ''; };
  const saveConn = async (ck) => {
    setConnSaving(p => ({...p, [ck]: true}));
    try {
      const fields = connections?.[ck]?.fields || [];
      const data = {};
      fields.forEach(f => { const ek = `${ck}.${f.key}`; data[f.key] = connEdits[ek] !== undefined ? connEdits[ek] : f.value || ''; });
      await api.put(`/admin/connections/${ck}`, data);
      toast.success('Saved'); await loadData();
    } catch { toast.error('Failed'); } finally { setConnSaving(p => ({...p, [ck]: false})); }
  };

  // REAL connection test
  const testConn = async (ck) => {
    setTesting(p => ({...p, [ck]: true}));
    setTestResults(p => ({...p, [ck]: null}));
    try {
      const r = await api.post(`/admin/connections/${ck}/test`);
      setTestResults(p => ({...p, [ck]: r.data}));
      r.data.success ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch (e) { setTestResults(p => ({...p, [ck]: {success: false, status: 'error', message: e.response?.data?.detail || 'Test failed'}})); toast.error('Test failed');
    } finally { setTesting(p => ({...p, [ck]: false})); }
  };

  // Source preview
  const fetchPreview = async (ck, entity) => {
    const key = `${ck}_${entity}`;
    setPreviewing(p => ({...p, [key]: true}));
    try {
      const r = await api.post(`/admin/connections/${ck}/preview`, { entity, limit: 5 });
      setPreviews(p => ({...p, [key]: r.data}));
      toast.success(`Fetched ${r.data.count} ${entity}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); setPreviews(p => ({...p, [key]: {error: e.response?.data?.detail || 'Failed'}}));
    } finally { setPreviewing(p => ({...p, [key]: false})); }
  };

  // Category mapping test
  const testMapping = async () => {
    if (!mappingTest.trim()) return;
    try {
      const lines = mappingTest.split('\n').filter(l => l.trim());
      const records = lines.map(l => { const [cat, ...title] = l.split('|'); return { category: cat?.trim() || '', item_title: title.join('|').trim() || '' }; });
      const r = await api.post('/admin/imports/preview-mapping', { records });
      setMappingResults(r.data);
    } catch { toast.error('Mapping test failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const renderTestResult = (ck) => {
    const r = testResults[ck];
    if (!r) return null;
    return (
      <div className={`mt-2 p-2 rounded-lg text-xs flex items-start gap-2 ${r.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
        {r.success ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5 flex-shrink-0" />}
        <div>
          <span className="font-medium" style={{color: r.success ? '#0E6B5D' : '#5A1F2A'}}>{r.status}</span>
          <p className="text-muted-foreground mt-0.5">{r.message}</p>
        </div>
      </div>
    );
  };

  const previewEntities = { woocommerce: ['orders','customers'], legacy_backend: ['certificates','orders','customers'], s3_legacy: ['assets'], payment_export: ['charges'] };

  const renderPreview = (ck) => {
    const entities = previewEntities[ck] || [];
    return (
      <div className="mt-3 pt-3 border-t border-stone/30">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Source Preview</p>
        <div className="flex gap-1 mb-2">
          {entities.map(ent => {
            const key = `${ck}_${ent}`;
            return (
              <Button key={ent} size="sm" variant="outline" className="text-[10px] h-6 px-2 gap-1" onClick={() => fetchPreview(ck, ent)} disabled={previewing[key]}>
                {previewing[key] ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Eye className="h-2.5 w-2.5" />} {ent}
              </Button>
            );
          })}
        </div>
        {entities.map(ent => {
          const key = `${ck}_${ent}`;
          const pv = previews[key];
          if (!pv) return null;
          if (pv.error) return <p key={key} className="text-xs text-red-600">{pv.error}</p>;
          return (
            <div key={key} className="mb-2">
              <p className="text-[10px] text-muted-foreground mb-1">{pv.count} {ent} fetched from {pv.source}</p>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {(pv.records || []).map((rec, i) => (
                  <div key={i} className="p-2 rounded bg-stone/5 border border-stone/20 text-[10px] font-mono overflow-x-auto">
                    {Object.entries(rec).filter(([k]) => k !== 'items').map(([k,v]) => (
                      <span key={k} className="inline-block mr-3"><span className="text-muted-foreground">{k}:</span> {String(v)}</span>
                    ))}
                    {rec.items && <div className="mt-1 text-muted-foreground">Items: {rec.items.map(it => it.name).join(', ')}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="import-dashboard">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Database className="h-4 w-4 text-ink" /><h1 className="text-base font-semibold text-ink">Import & Migration</h1></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="text-xs gap-1" onClick={runReconciliation} disabled={processing}><RefreshCw className="h-3 w-3" /> Reconcile</Button>
          <Button size="sm" className="text-xs gap-1" onClick={() => setShowCreate(true)} data-testid="create-run-btn"><Plus className="h-3 w-3" /> New Import Run</Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          {[{l:'Import Runs',v:stats.total_runs,icon:Database},{l:'Legacy Certs',v:stats.legacy_certificates,icon:FileText},{l:'Legacy Orders',v:stats.legacy_orders,icon:Clock},{l:'Legacy Assets',v:stats.legacy_assets,icon:Database},{l:'Unresolved Errors',v:stats.unresolved_errors,icon:AlertCircle,danger:stats.unresolved_errors>0}].map((m,i) => {
            const Icon = m.icon;
            return <Card key={i} className="bg-white border-stone/50"><CardContent className="p-3"><div className="flex items-center gap-1.5 mb-1"><Icon className={`h-3 w-3 ${m.danger?'text-red-500':'text-muted-foreground'}`} /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.l}</span></div><p className={`text-lg font-semibold ${m.danger?'text-red-600':'text-ink'}`}>{m.v}</p></CardContent></Card>;
          })}
        </div>
      )}

      {showCreate && (
        <Card className="bg-white border-stone/50 mb-4"><CardContent className="p-4">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">New Import Run</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Run Type</Label><Select value={form.run_type} onValueChange={v => setForm({...form, run_type: v})}><SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="full">Full Migration</SelectItem><SelectItem value="customers">Customers</SelectItem><SelectItem value="orders">Orders</SelectItem>{/* Certificates only import from the Legacy Backend (D3): Woo carries no legacy cert codes. */}{form.source_system === 'legacy_backend' && <SelectItem value="certificates">Certificates</SelectItem>}<SelectItem value="assets">S3 Assets</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">Source System</Label><Select value={form.source_system} onValueChange={v => setForm({...form, source_system: v, run_type: (v !== 'legacy_backend' && form.run_type === 'certificates') ? 'orders' : form.run_type})}><SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="woocommerce">WordPress/WooCommerce</SelectItem><SelectItem value="legacy_backend">Legacy Backend</SelectItem><SelectItem value="s3_legacy">S3 Legacy</SelectItem><SelectItem value="payment_export">Payment Export</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 h-8 text-xs" placeholder="Optional" /></div>
            <div className="flex items-center gap-3 mt-5"><Switch checked={form.is_dry_run} onCheckedChange={v => setForm({...form, is_dry_run: v})} /><span className="text-xs">{form.is_dry_run ? 'Dry Run' : 'Live Import'}</span></div>
          </div>
          <div className="flex gap-2 mt-3"><Button size="sm" className="text-xs gap-1" onClick={createRun}><Play className="h-3 w-3" /> Create</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => setShowCreate(false)}>Cancel</Button></div>
        </CardContent></Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-9 mb-4">
          <TabsTrigger value="connections" className="text-xs gap-1" data-testid="connections-tab"><Settings className="h-3 w-3" /> Connections</TabsTrigger>
          <TabsTrigger value="mapping" className="text-xs gap-1"><Tag className="h-3 w-3" /> Mapping</TabsTrigger>
          <TabsTrigger value="runs" className="text-xs gap-1"><Database className="h-3 w-3" /> Runs ({runs.length})</TabsTrigger>
          <TabsTrigger value="errors" className="text-xs gap-1"><AlertCircle className="h-3 w-3" /> Errors ({errors.length})</TabsTrigger>
          <TabsTrigger value="reconciliation" className="text-xs gap-1"><RefreshCw className="h-3 w-3" /> Reconciliation</TabsTrigger>
        </TabsList>

        {/* Connections Tab */}
        <TabsContent value="connections">
          <div className="space-y-4">
            {connections && Object.entries(connections).map(([ck, conn]) => (
              <Card key={ck} className="bg-white border-stone/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold text-ink">{conn.name}</h3>
                      {testResults[ck] && (
                        <Badge variant="outline" className={`text-[9px] ${testResults[ck].success ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                          {testResults[ck].success ? <><Wifi className="h-2.5 w-2.5 mr-0.5" /> Connected</> : <><WifiOff className="h-2.5 w-2.5 mr-0.5" /> {testResults[ck].status}</>}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => testConn(ck)} disabled={testing[ck]} data-testid={`test-conn-${ck}`}>
                        {testing[ck] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />} Test
                      </Button>
                      <Button size="sm" className="h-7 text-xs gap-1" onClick={() => saveConn(ck)} disabled={connSaving[ck]}><Save className="h-3 w-3" /> Save</Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{conn.description}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {conn.fields?.map(field => (
                      <div key={field.key}>
                        <Label className="text-xs">{field.label}</Label>
                        <Input type={field.type === 'password' ? 'password' : 'text'} value={getConnValue(ck, field)} onChange={e => updateConnField(ck, field.key, e.target.value)} placeholder={field.placeholder} className="mt-1 h-8 text-xs" data-testid={`conn-${ck}-${field.key}`} />
                        {field.display_value && field.type === 'password' && !connEdits[`${ck}.${field.key}`] && (
                          <p className="text-[9px] text-muted-foreground mt-0.5 font-mono">Current: {field.display_value}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {renderTestResult(ck)}
                  {renderPreview(ck)}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Mapping Tab */}
        <TabsContent value="mapping">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-white border-stone/50">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Tag className="h-3 w-3" /> Category Mapping Rules</h3>
                {mappingRules && (
                  <div className="space-y-3 text-xs">
                    <div>
                      <p className="text-muted-foreground mb-1">Direct maps (auto-resolved):</p>
                      <div className="flex flex-wrap gap-1">{mappingRules.direct_maps.map(c => <Badge key={c} variant="outline" className="text-[9px]">{c}</Badge>)}</div>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Combined patterns (need keyword split):</p>
                      <div className="flex flex-wrap gap-1">{mappingRules.combined_patterns.map(c => <Badge key={c} variant="outline" className="text-[9px] bg-amber-50 text-amber-700">{c}</Badge>)}</div>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Clothing keywords:</p>
                      <p className="text-[10px] text-muted-foreground">{mappingRules.clothing_keywords.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Accessories keywords:</p>
                      <p className="text-[10px] text-muted-foreground">{mappingRules.accessories_keywords.join(', ')}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="bg-white border-stone/50">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Search className="h-3 w-3" /> Test Mapping</h3>
                <p className="text-[10px] text-muted-foreground mb-2">Enter one record per line: <code className="bg-stone/20 px-1 rounded">category | item title</code></p>
                <textarea value={mappingTest} onChange={e => setMappingTest(e.target.value)} placeholder={"Clothing & Accessories | Designer Jacket\nClothing & Accessories | Leather Belt\nClothing & Accessories | Mystery Item\nHandbags | Chanel Flap Bag"} className="w-full p-2 rounded border border-stone text-xs font-mono min-h-[80px] resize-y focus:outline-none focus:ring-1 focus:ring-ring/30" data-testid="mapping-test-input" />
                <Button size="sm" className="mt-2 text-xs gap-1 w-full" onClick={testMapping} data-testid="run-mapping-test"><Play className="h-3 w-3" /> Test Mapping</Button>
                {mappingResults && (
                  <div className="mt-3 space-y-1">
                    <div className="flex gap-3 text-xs mb-2">
                      <span className="text-emerald-600 font-medium">{mappingResults.mapped} mapped</span>
                      <span className="text-amber-600 font-medium">{mappingResults.ambiguous} ambiguous</span>
                    </div>
                    {mappingResults.results.map((r, i) => (
                      <div key={i} className={`p-1.5 rounded text-[10px] flex items-center gap-2 ${r.is_ambiguous ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                        {r.is_ambiguous ? <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0" /> : <CheckCircle className="h-3 w-3 text-emerald-600 flex-shrink-0" />}
                        <span className="font-mono">{r.input_category}</span>
                        <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" />
                        <span className="font-medium">{r.mapped_to || 'AMBIGUOUS'}</span>
                        {r.reason && <span className="text-muted-foreground ml-auto">{r.reason}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Runs Tab */}
        <TabsContent value="runs">
          {selectedRun ? (
            <Card className="bg-white border-stone/50"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div><h3 className="text-sm font-semibold text-ink">{selectedRun.run_type} - {selectedRun.source_system}</h3><span className="text-[10px] font-mono text-muted-foreground">{selectedRun.id}</span></div>
                <div className="flex items-center gap-2"><Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[selectedRun.status]||''}`}>{selectedRun.status}</Badge>{selectedRun.is_dry_run&&<Badge variant="outline" className="text-[10px]">Dry Run</Badge>}{(selectedRun.status==='pending'||selectedRun.status==='failed')&&<Button size="sm" className="text-xs h-7 gap-1" onClick={()=>fetchAndProcess(selectedRun)} disabled={processing} data-testid="fetch-process-detail"><Play className="h-3 w-3"/> {selectedRun.is_dry_run?'Dry Run':'Fetch & Process'}</Button>}<Button size="sm" variant="outline" className="text-xs h-7" onClick={()=>setSelectedRun(null)}>Back</Button></div>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-4 text-sm">
                <div><span className="text-muted-foreground text-xs">Total</span><p className="font-semibold">{selectedRun.total_records}</p></div>
                <div><span className="text-muted-foreground text-xs">Imported</span><p className="font-semibold text-emerald-600">{selectedRun.imported_count}</p></div>
                <div><span className="text-muted-foreground text-xs">Skipped</span><p className="font-semibold text-blue-600">{selectedRun.skipped_count}</p></div>
                <div><span className="text-muted-foreground text-xs">Errors</span><p className="font-semibold text-red-600">{selectedRun.error_count}</p></div>
              </div>
              <Separator className="mb-3" /><h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Event Log</h4>
              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {(selectedRun.events||[]).map((e,i) => (<div key={i} className="flex items-center gap-2 text-xs py-1">{e.event_type==='created'&&<CheckCircle className="h-3 w-3 text-emerald-500"/>}{e.event_type==='deduped'&&<RotateCcw className="h-3 w-3 text-blue-500"/>}{e.event_type==='error'&&<XCircle className="h-3 w-3 text-red-500"/>}<span className="text-ink">{e.message}</span><span className="text-muted-foreground ml-auto text-[10px]">{e.entity_type}</span></div>))}
                {(!selectedRun.events||selectedRun.events.length===0)&&<p className="text-xs text-muted-foreground py-4 text-center">No events</p>}
              </div>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {runs.length===0?(<Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><Database className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30"/><p className="text-sm text-muted-foreground">No import runs yet.</p></CardContent></Card>
              ):runs.map(r=>(<Card key={r.id} className="bg-white border-stone/50 cursor-pointer hover:shadow-sm transition-shadow" onClick={()=>viewRun(r.id)}><CardContent className="p-3 flex items-center justify-between"><div><div className="flex items-center gap-2"><span className="text-sm font-medium text-ink">{r.run_type}</span><Badge variant="outline" className="text-[10px]">{r.source_system}</Badge>{r.is_dry_run&&<Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Dry Run</Badge>}</div><span className="text-[10px] text-muted-foreground">{r.created_at?new Date(r.created_at).toLocaleString():''}</span></div><div className="flex items-center gap-3"><div className="text-right text-[10px]"><span className="text-emerald-600">{r.imported_count} imported</span>{r.skipped_count>0&&<span className="text-blue-600 ml-2">{r.skipped_count} skipped</span>}{r.error_count>0&&<span className="text-red-600 ml-2">{r.error_count} errors</span>}</div>{(r.status==='pending'||r.status==='failed')&&<Button size="sm" className="h-7 text-[10px] gap-1" onClick={(e)=>fetchAndProcess(r,e)} disabled={processing} data-testid={`fetch-process-${r.id}`}><Play className="h-3 w-3"/> {r.is_dry_run?'Dry Run':'Fetch & Process'}</Button>}<Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[r.status]||''}`}>{r.status}</Badge><Eye className="h-3.5 w-3.5 text-muted-foreground"/></div></CardContent></Card>))}
            </div>
          )}
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors">
          <div className="space-y-2">
            {errors.length===0?(<Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500/30"/><p className="text-sm text-muted-foreground">No unresolved import errors.</p></CardContent></Card>
            ):errors.map(err=>(<Card key={err.id} className="bg-white border-stone/50 border-l-4 border-l-red-400"><CardContent className="p-3"><div className="flex items-start justify-between"><div><div className="flex items-center gap-2 mb-1"><Badge variant="outline" className="text-[10px] bg-red-50 text-red-700">{err.error_type}</Badge><span className="text-xs text-ink font-medium">{err.entity_type}/{err.source_pk}</span></div><p className="text-xs text-muted-foreground">{err.error_message}</p>{err.source_data&&<details className="mt-1"><summary className="text-[10px] text-muted-foreground cursor-pointer">Source data</summary><pre className="text-[10px] mt-1 p-2 bg-stone/10 rounded overflow-x-auto">{JSON.stringify(err.source_data,null,2)}</pre></details>}</div><div className="flex gap-1 flex-shrink-0 ml-3">{err.error_type==='category_mapping'&&<><Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={()=>manualCategoryResolve(err.id,'clothing')} disabled={processing}>Clothing</Button><Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={()=>manualCategoryResolve(err.id,'accessories')} disabled={processing}>Accessories</Button></>}<Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={()=>resolveError(err.id,'Manually resolved')} disabled={processing}>Resolve</Button></div></div></CardContent></Card>))}
          </div>
        </TabsContent>

        {/* Reconciliation Tab */}
        <TabsContent value="reconciliation">
          {reconcReports.length===0?(<Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><RefreshCw className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30"/><p className="text-sm text-muted-foreground mb-3">No reconciliation reports yet.</p><Button size="sm" className="text-xs gap-1" onClick={runReconciliation} disabled={processing}><RefreshCw className="h-3 w-3"/> Run Reconciliation</Button></CardContent></Card>
          ):reconcReports.map(rpt=>(<Card key={rpt.id} className="bg-white border-stone/50"><CardContent className="p-4"><div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold text-ink">{rpt.report_type} Report</h3><Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[rpt.status]||''}`}>{rpt.status}</Badge></div>{rpt.summary&&<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{Object.entries(rpt.summary).map(([k,v])=><div key={k} className="text-xs"><span className="text-muted-foreground">{k.replace(/_/g,' ')}</span><p className="font-medium text-ink">{typeof v==='number'?v.toLocaleString():String(v)}</p></div>)}</div>}</CardContent></Card>))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
